import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import DevicesIcon from "@mui/icons-material/DevicesOutlined"
import LogoutIcon from "@mui/icons-material/LogoutOutlined"

import { userApi } from "../../api"
import { LoadingRows } from "../../components/LoadingState"
import { formatDateTime, relativeTime } from "../../utils/format"
import { rise } from "../../motion"
import { shortDevice } from "./PersonActivity"

/**
 * Where somebody is signed in, and how their sign-ins have gone.
 *
 * Two lists for two questions. Devices answers "is this account in use right
 * now, and where" — the thing to check when a laptop goes missing, with a way
 * to end any one session or all of them. Sign-ins answers "has anybody been
 * trying to get in", which is why failures are listed beside successes.
 */
export default function PersonSessions({ person, isSelf, onChanged }) {
  const [sessions, setSessions] = useState(null)
  const [logins, setLogins] = useState(null)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      const [sessionData, loginData] = await Promise.all([userApi.sessions(person.id), userApi.logins(person.id, { limit: 25 })])
      setSessions(sessionData.sessions || [])
      setLogins(loginData.logins || [])
    } catch (err) {
      setError(err.userMessage || "Sessions could not be loaded.")
      setSessions((current) => current || [])
      setLogins((current) => current || [])
    }
  }, [person.id])

  useEffect(() => {
    load()
  }, [load])

  const end = async (session) => {
    setBusy(session ? session.id : "all")
    setError(null)
    try {
      if (session) await userApi.endSession(person.id, session.id)
      else await userApi.endSessions(person.id)
      await load()
      onChanged?.(session ? "That device was signed out." : "Signed out everywhere.")
    } catch (err) {
      setError(err.userMessage || "That could not be signed out.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card sx={rise(0)}>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h3" component="h2">
                Signed in on
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Every device holding a live session for this account.
              </Typography>
            </Box>
            {sessions && sessions.length > 0 && !isSelf ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<LogoutIcon />}
                disabled={busy !== null}
                onClick={() => end(null)}
              >
                {busy === "all" ? "Signing out…" : "Sign out everywhere"}
              </Button>
            ) : null}
          </Stack>

          {sessions === null ? (
            <LoadingRows rows={2} height={40} />
          ) : sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Not signed in anywhere right now.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {sessions.map((session, index) => (
                <Stack
                  key={session.id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ p: 1.25, borderRadius: 2, border: 1, borderColor: "divider", ...rise(index) }}
                >
                  <DevicesIcon sx={{ color: "text.secondary" }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {shortDevice(session.device) || "Unknown device"}
                      {session.platform ? (
                        <Chip size="small" label={session.platform} sx={{ height: 18, fontSize: 10, ml: 1 }} />
                      ) : null}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {[session.ip_address, `active ${relativeTime(session.last_active)}`, `signed in ${formatDateTime(session.created_at)}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                  {!isSelf ? (
                    <Tooltip title="Sign out of this device">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => end(session)}
                          disabled={busy !== null}
                          aria-label={`Sign out of ${shortDevice(session.device) || "this device"}`}
                        >
                          <LogoutIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          )}
          {isSelf ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              Your own sessions are managed in Settings, where signing out of this one is not an accident.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      <Card sx={rise(1)}>
        <CardContent>
          <Typography variant="h3" component="h2">
            Sign-in history
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            The last 25 attempts, failed ones included.
          </Typography>
          {logins === null ? (
            <LoadingRows rows={3} height={32} />
          ) : logins.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No sign-ins recorded.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logins.map((login) => (
                    <TableRow key={login.id}>
                      <TableCell title={formatDateTime(login.created_at)}>{relativeTime(login.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={login.success ? "Signed in" : login.failure_reason ? String(login.failure_reason).replace(/_/g, " ") : "Failed"}
                          color={login.success ? "success" : "error"}
                          variant={login.success ? "outlined" : "filled"}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={login.device || ""}>
                          {shortDevice(login.device) || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                          {login.ip_address || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}
